import { Injectable } from '@angular/core';
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {Observable} from "rxjs";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {CsvReportField} from "../models/csv-report-field.interface";

/**
 * Serwis narzędziowy obsługujący niestandardowe konfiguracje placówek.
 * Głównym obszarem jego działania jest zarządzanie strukturą i pobieraniem
 * codziennych raportów CSV oraz synchronizacja czasu z serwerem.
 */
@Injectable({
  providedIn: 'root'
})
export class CustomService {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) { }

  /**
   * Pobiera wygenerowany raport dziennego podsumowania wizyt/płatności
   * dla określonej kliniki w zadanym, relatywnym przedziale czasu.
   *
   * @param vetcode Unikalny numer identyfikacyjny kliniki (ID).
   * @param dayTag Znacznik relatywnego czasu określający, z jakiego dnia pobrać dane
   * ('today' - dzisiaj, 'yesterday' - wczoraj, 'day-before-yesterday' - przedwczoraj).
   * @returns Observable emitujący surowy ładunek danych raportu dostosowany do wymogów kliniki.
   */
  getCustom(vetcode: number, dayTag?: 'today' | 'yesterday' | 'day-before-yesterday'): Observable<any> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/custom/${dayTag}/${vetcode}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje nową konfigurację pól (kolumn) dla niestandardowych raport dziennego podsumowanie wizyt/płatności.
   * Pozwala to placówkom na decydowanie, które dane mają znajdować się w ich codziennych raportach.
   *
   * @param requestObj Obiekt zawierający identyfikator kliniki oraz wybraną tablicę pól raportu (CsvReportField).
   * @returns Observable potwierdzający poprawny zapis konfiguracji lub rzucający błąd.
   */
  setCustom(requestObj: {vetcodeId: number; csvReportFields: CsvReportField[]}) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/set-custom/${requestObj.vetcodeId}`,
      requestObj).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Odpytuje serwer o dokładny, aktualny czas (Current Server Time)
   *
   * @returns Observable emitujący dokładny czas z serwera.
   */
  getServerTime() {
    return this.apiService.get<VetineoApiResponse>('/dashboard/get-server-time').pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.currentServerTime;
      } else {
        return undefined;
      }
    }));
  }

}
